import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { SupplierSection } from './form-sections/supplier-section';
import { TechnicalSection } from './form-sections/technical-section';
import { LocationSection } from './form-sections/location-section';
import { CostsSection } from './form-sections/costs-section';
import { NotesSection } from './form-sections/notes-section';
import { PhotoUploadSection } from '@ooh/screens';
import { useAddForm } from '../hooks/use-add-form';

export interface MediaAddFormProps {
  /** Forwarded from the route's own search — see CU-86caypxaw. */
  addToOfferId?: string;
}

export function MediaAddForm({ addToOfferId }: MediaAddFormProps) {
  const { t: tCommon } = useTranslation('common');
  const {
    t,
    register,
    watch,
    setValue,
    errors,
    onSubmit,
    isPending,
    isSuccess,
    photos,
    fileInputRef,
    photoCountError,
    handleFileChange,
    handleRemovePhoto,
    handleAddAnother,
    handleGoToCatalogNow,
    supplierOptions,
    formatOptions,
    isSuppliersLoading,
    isFormatsLoading,
    filteredCounties,
    handleRegionChange,
    area,
    voivodeshipOptions,
    navigate,
    catalogSearch,
    isDimensionRequired,
  } = useAddForm(addToOfferId);

  const isLoading = isPending || isSuppliersLoading || isFormatsLoading;
  const btnVariant = 'default' as const;
  const btnIconTop = isPending ? (
    <Loader2 className="h-4 w-4 animate-spin" data-testid="save-button-spinner" />
  ) : (
    <Save className="h-4 w-4" />
  );
  const btnIcon = isPending ? (
    <Loader2 className="h-4 w-4 animate-spin" />
  ) : (
    <Save className="h-4 w-4" />
  );
  const btnLabel = isPending ? t('form.saveButton.saving') : t('form.saveButton.idle');

  return (
    <div className="p-6 max-w-4xl" data-testid="media-add-form">
      <div className="flex items-center gap-3 mb-5">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => void navigate({ to: '/media-units', search: catalogSearch })}
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          {tCommon('actions.backToCatalog')}
        </Button>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" data-testid="form-title">
            {t('form.add.title')}
          </h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            {t('form.add.subtitle')} <span className="text-destructive">*</span>{' '}
            {t('form.add.requiredHint')}
          </p>
        </div>
        <Button
          type="button"
          size="default"
          variant={btnVariant}
          disabled={isLoading}
          data-testid="save-button"
          onClick={() => void onSubmit()}
        >
          {btnIconTop}
          {btnLabel}
        </Button>
      </div>

      {isSuccess ? (
        <div
          className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 flex items-center gap-3 mb-6"
          data-testid="success-banner"
        >
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="flex-1">
            <p className="text-[14px] font-medium text-emerald-800">{t('form.successAdd.title')}</p>
            <p className="text-[12px] text-emerald-600 mt-0.5">
              {t('form.successAdd.description')}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="success-add-another-button"
              onClick={handleAddAnother}
            >
              {t('form.successAdd.addAnother')}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              data-testid="success-go-to-catalog-button"
              onClick={handleGoToCatalogNow}
            >
              {t('form.successAdd.goToCatalog')}
            </Button>
          </div>
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <div className="space-y-6">
          <SupplierSection
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            supplierOptions={supplierOptions}
            isSuppliersLoading={isSuppliersLoading}
            t={t}
          />
          <PhotoUploadSection
            mode="create"
            photos={photos}
            onFileChange={handleFileChange}
            onRemovePhoto={handleRemovePhoto}
            fileInputRef={fileInputRef}
            photoCountError={photoCountError}
            t={t}
          />
          <TechnicalSection
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            formatOptions={formatOptions}
            computedArea={area}
            t={t}
            isDimensionRequired={isDimensionRequired}
          />
          <LocationSection
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            handleRegionChange={handleRegionChange}
            filteredCounties={filteredCounties}
            voivodeshipOptions={voivodeshipOptions}
            t={t}
          />
          <CostsSection
            register={register}
            errors={errors}
            t={t}
            subtitle={t('form.sections.costsSubtitle')}
          />
          <NotesSection register={register} errors={errors} t={t} />
          <div className="flex items-center justify-between pt-2 pb-8">
            <Button
              type="button"
              variant="outline"
              data-testid="cancel-button"
              onClick={() => void navigate({ to: '/media-units', search: catalogSearch })}
            >
              {tCommon('actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant={btnVariant}
              disabled={isLoading}
              data-testid="save-button-secondary"
            >
              {btnIcon}
              {btnLabel}
            </Button>
          </div>
        </div>
      </form>
      {isLoading && !isPending ? <div className="sr-only">{t('form.photos.loading')}</div> : null}
    </div>
  );
}
